/**
 * Treydora marketplace — cinematic imagery map (bundled + public/).
 * Used for heroes, category rails, preview storefronts, and intelligent listing fallbacks.
 * Never replaces backend-provided listing/store banner URLs when present.
 */

import {
  storefrontHeroBackground21x9,
  storefrontHeroCityNight,
  storefrontParcelTowerArt,
  storefrontCommerceDistrictPanel,
  storefrontCommerceCityDay,
} from "../assets/dashboard/storefront";

import nftComic from "../assets/img/treydora_redesign/homepage/NFTComic.png";
import treydorexToken from "../assets/img/treydora_redesign/homepage/treydorextoken.png";
import koraxosPromo from "../assets/img/treydora_redesign/homepage/Koraxos.png";
import osharWorld from "../assets/img/treydora_redesign/homepage/OsharWorld.png";
import vendorStall from "../assets/img/treydora_redesign/VR_Realestate/vendor-stall-storefront.png";
import farmBioluminescent from "../assets/img/treydora_redesign/VR_Realestate/farm-bioluminescent-district.png";

/** Absolute URL for files served from `public/` (CRA). */
export function marketplacePublicUrl(pathFromPublicRoot) {
  const base = process.env.PUBLIC_URL || "";
  const p = pathFromPublicRoot.startsWith("/") ? pathFromPublicRoot : `/${pathFromPublicRoot}`;
  return `${base}${p}`;
}

/** Primary wide hero for /marketplace (bundled production art). */
export const MARKETPLACE_HERO_PRIMARY = storefrontHeroBackground21x9;

/** Secondary parallax / depth layer (Oshar skyline). */
export const MARKETPLACE_HERO_DEPTH = marketplacePublicUrl("/images/world/oshar/oshar-panorama.png");

/** Mid-page “bazaar corridor” banner. */
export const MARKETPLACE_FEATURED_BANNER = storefrontHeroCityNight;

/** Empty catalog / vault state. */
export const MARKETPLACE_VAULT_EMPTY_ART = marketplacePublicUrl("/assets/dashboard/01_hero_oshar_city_spires.png");

/** Category filter chips (id matches `PREVIEW_STORE_CATEGORIES`). */
export const CATEGORY_MARKETPLACE_THUMB = {
  all: osharWorld,
  digital_art: farmBioluminescent,
  game_assets: marketplacePublicUrl("/assets/treydorex-sales/06_access_icon_game.png"),
  ai_tools: marketplacePublicUrl("/assets/dashboard/07_ai_chronicle_lore_terminal.png"),
  lore_comics: nftComic,
  music_audio: marketplacePublicUrl("/images/world/oshar/oshar-live-events.png"),
  education: marketplacePublicUrl("/assets/library/01_library_hero_great_hall.png"),
  fashion_merch: marketplacePublicUrl("/images/world/oshar/oshar-shopping.png"),
  vr_experiences: marketplacePublicUrl("/assets/dashboard/08_treydoravr_gateway_access.png"),
  services: marketplacePublicUrl("/assets/dashboard/03_vendor_stall_card.png"),
};

export function categoryMarketplaceThumb(categoryId) {
  if (!categoryId) return osharWorld;
  return CATEGORY_MARKETPLACE_THUMB[categoryId] || osharWorld;
}

/** Preview storefront banner photos (fixtures) by commerce category. */
export const PREVIEW_STORE_BANNER = {
  game_assets: marketplacePublicUrl("/assets/treydorex-sales/02_game_poster_koraxos_no_text.png"),
  lore_comics: nftComic,
  ai_tools: marketplacePublicUrl("/assets/dashboard/07_ai_chronicle_lore_terminal.png"),
  digital_art: farmBioluminescent,
  music_audio: marketplacePublicUrl("/images/world/oshar/oshar-coliseum.png"),
  education: marketplacePublicUrl("/assets/library/02_library_tomes_reader_background.png"),
  fashion_merch: marketplacePublicUrl("/images/world/oshar/oshar-shopping.png"),
  vr_experiences: marketplacePublicUrl("/assets/dashboard/08_treydoravr_gateway_access.png"),
  services: vendorStall,
};

export function previewStoreBannerImage(categoryId) {
  if (!categoryId) return storefrontCommerceCityDay;
  return PREVIEW_STORE_BANNER[categoryId] || storefrontCommerceCityDay;
}

const OSHAR_CITY = marketplacePublicUrl("/images/world/oshar/oshar-hero.png");
const TRDX_CITY = marketplacePublicUrl("/assets/treydorex-sales/01_trdx_section_background_city.png");
const COMMERCIAL_CARD = marketplacePublicUrl("/assets/dashboard/02_commercial_storefront_card.png");

/**
 * When API store has no `bannerUrl`, pick a cinematic default from category / copy heuristics.
 * @param {{ categories?: string[]; storeName?: string; description?: string }} store
 */
export function resolveApiStoreBanner(store) {
  const hay = [store?.storeName, store?.description, ...(Array.isArray(store?.categories) ? store.categories : [])]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const t = (re) => re.test(hay);
  if (t(/comic|lore|chronicle|book|issue/)) return nftComic;
  if (t(/game|skin|forge|armory|loadout/)) return marketplacePublicUrl("/assets/treydorex-sales/02_game_poster_koraxos_no_text.png");
  if (t(/ai|prompt|copilot|automation/)) return marketplacePublicUrl("/assets/dashboard/07_ai_chronicle_lore_terminal.png");
  if (t(/audio|music|sound|score/)) return marketplacePublicUrl("/images/world/oshar/oshar-live-events.png");
  if (t(/education|course|lesson|library/)) return marketplacePublicUrl("/assets/library/01_library_hero_great_hall.png");
  if (t(/fashion|merch|apparel/)) return marketplacePublicUrl("/images/world/oshar/oshar-shopping.png");
  if (t(/vr|immersive|spatial/)) return marketplacePublicUrl("/assets/dashboard/08_treydoravr_gateway_access.png");
  if (t(/real\s*estate|parcel|property|residence|nft\s*real|oshar\s*real/)) return storefrontParcelTowerArt;
  if (t(/art|gallery|canvas|print/)) return farmBioluminescent;
  if (t(/vendor|stall|service|bazaar/)) return vendorStall;
  return COMMERCIAL_CARD;
}

/**
 * Catalog cards: only used when product has no thumbnail / preview image from API.
 * @param {{ category?: string; title?: string; storeName?: string; description?: string }} product
 */
export function resolveProductVisual(product) {
  const hay = [product?.category, product?.title, product?.storeName, product?.description]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const t = (re) => re.test(hay);
  if (t(/real\s*estate|parcel|property|residence|vr\s*real|oshar\s*real|token\s*id/)) return storefrontParcelTowerArt;
  if (t(/comic|koraxos|chapter|issue|graphic|lore/)) return nftComic;
  if (t(/treydorex|\btrdx\b|token|coin|\beth\b|currency/)) return treydorexToken;
  if (t(/koraxos|superhero|hero\s*#/) && !t(/comic/)) return koraxosPromo;
  if (t(/music|audio|sound|score|ost/)) return marketplacePublicUrl("/images/world/oshar/oshar-coliseum.png");
  if (t(/game|asset|skin|hud|forge|loadout/)) return marketplacePublicUrl("/assets/treydorex-sales/06_access_icon_game.png");
  if (t(/art|canvas|print|gallery|edition/)) return storefrontCommerceDistrictPanel;
  if (t(/education|course|lesson|study|workbook/)) return marketplacePublicUrl("/assets/library/02_library_tomes_reader_background.png");
  if (t(/vr|immersive|world|oshar(?!\s*real)/)) return OSHAR_CITY;
  if (t(/token|sale|access|pass/)) return TRDX_CITY;
  return storefrontCommerceCityDay;
}
